/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/documentSendPayload.ts
 * FILE_ID: QUOTE-CLAIMS-SEND-PAYLOAD-001
 * PURPOSE: Freeze one idempotency key into each client-send mutation payload.
 * WHY: Transport retries must reuse the same key instead of sending twice.
 */

import type { DocumentSendRequest } from './RecipientPickerDialog'

export type DocumentSendPayload = DocumentSendRequest & {
  idempotency_key: string
}

export interface DocumentSendAttempt {
  fingerprint: string
  payload: DocumentSendPayload
}

function randomToken(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function createDocumentSendPayload(
  documentId: string,
  request: DocumentSendRequest,
  nonce = randomToken(),
): DocumentSendPayload {
  return {
    ...request,
    idempotency_key: `${documentId}-${nonce}`.slice(0, 128),
  }
}

export function createOrReuseDocumentSendAttempt(
  documentId: string,
  request: DocumentSendRequest,
  previous: DocumentSendAttempt | null,
  nonce = randomToken(),
): DocumentSendAttempt {
  const fingerprint = JSON.stringify({ documentId, request })
  if (previous?.fingerprint === fingerprint) return previous
  return {
    fingerprint,
    payload: createDocumentSendPayload(documentId, request, nonce),
  }
}
